// Client-side testimonials + feedback store (localStorage).
// Lightweight by design — admin moderation lives in /admin-testimonials.

export type Testimonial = {
  id: string;
  name: string;
  rating: number; // 1–5
  quote: string;
  feature?: string;
  date: string; // ISO
  approved: boolean;
  source: "seed" | "member";
  helpedMost?: string;
  mostUsed?: string;
  wantNext?: string;
};

export type EventFeedback = {
  id: string;
  slug: string;
  eventTitle: string;
  rating: number;
  comment?: string;
  date: string;
};

export type FeatureFeedback = {
  id: string;
  feature: string;
  helpful: boolean;
  comment?: string;
  date: string;
};

const KEY_SUBMISSIONS = "rt_testimonials_v1";
const KEY_EVENT_FB = "rt_event_feedback_v1";
const KEY_FEATURE_FB = "rt_feature_feedback_v1";

const isBrowser = typeof window !== "undefined";

function read<T>(key: string): T[] {
  if (!isBrowser) return [];
  try {
    return JSON.parse(localStorage.getItem(key) || "[]") as T[];
  } catch {
    return [];
  }
}
function write<T>(key: string, value: T[]) {
  if (!isBrowser) return;
  localStorage.setItem(key, JSON.stringify(value));
}
function uid() {
  return Math.random().toString(36).slice(2, 10);
}

// ───────── Seed testimonials ─────────
// Intentionally EMPTY. Recovery Trauma™ policy: only display real, verified
// reviews (e.g. via the live Trustpilot TrustBox widget) or testimonials
// submitted and approved through the member flow. Never fabricate, AI-generate,
// paraphrase, or seed marketing testimonials.
export const SEED_TESTIMONIALS: Testimonial[] = [];


// ───────── Testimonials (read / write) ─────────
export function getAllTestimonials(): Testimonial[] {
  return [...SEED_TESTIMONIALS, ...read<Testimonial>(KEY_SUBMISSIONS)];
}
export function getApprovedTestimonials(): Testimonial[] {
  return getAllTestimonials().filter((t) => t.approved);
}
export function submitTestimonial(input: {
  name: string;
  rating: number;
  quote: string;
  feature?: string;
  helpedMost?: string;
  mostUsed?: string;
  wantNext?: string;
}): Testimonial {
  const t: Testimonial = {
    id: uid(),
    name: input.name.trim() || "Anonymous member",
    rating: Math.min(5, Math.max(1, input.rating)),
    quote: input.quote.trim().slice(0, 600),
    feature: input.feature?.trim().slice(0, 80),
    helpedMost: input.helpedMost?.trim().slice(0, 280),
    mostUsed: input.mostUsed?.trim().slice(0, 120),
    wantNext: input.wantNext?.trim().slice(0, 280),
    date: new Date().toISOString(),
    approved: false,
    source: "member",
  };
  const current = read<Testimonial>(KEY_SUBMISSIONS);
  write(KEY_SUBMISSIONS, [t, ...current]);
  return t;
}
export function setTestimonialApproval(id: string, approved: boolean) {
  if (id.startsWith("seed-")) return; // seeds locked
  const list = read<Testimonial>(KEY_SUBMISSIONS).map((t) =>
    t.id === id ? { ...t, approved } : t
  );
  write(KEY_SUBMISSIONS, list);
}
export function deleteTestimonial(id: string) {
  if (id.startsWith("seed-")) return;
  write(
    KEY_SUBMISSIONS,
    read<Testimonial>(KEY_SUBMISSIONS).filter((t) => t.id !== id)
  );
}

// ───────── Event feedback ─────────
export function submitEventFeedback(input: {
  slug: string;
  eventTitle: string;
  rating: number;
  comment?: string;
}) {
  const fb: EventFeedback = {
    id: uid(),
    slug: input.slug,
    eventTitle: input.eventTitle,
    rating: Math.min(5, Math.max(1, input.rating)),
    comment: input.comment?.trim().slice(0, 600),
    date: new Date().toISOString(),
  };
  write(KEY_EVENT_FB, [fb, ...read<EventFeedback>(KEY_EVENT_FB)]);
}
export function getEventFeedback(): EventFeedback[] {
  return read<EventFeedback>(KEY_EVENT_FB);
}
export function hasGivenEventFeedback(slug: string): boolean {
  return getEventFeedback().some((f) => f.slug === slug);
}

// ───────── Feature feedback ─────────
export function submitFeatureFeedback(input: {
  feature: string;
  helpful: boolean;
  comment?: string;
}) {
  const fb: FeatureFeedback = {
    id: uid(),
    feature: input.feature,
    helpful: input.helpful,
    comment: input.comment?.trim().slice(0, 600),
    date: new Date().toISOString(),
  };
  write(KEY_FEATURE_FB, [fb, ...read<FeatureFeedback>(KEY_FEATURE_FB)]);
}
export function getFeatureFeedback(): FeatureFeedback[] {
  return read<FeatureFeedback>(KEY_FEATURE_FB);
}

// ───────── CSV export helper ─────────
export function toCsv<T extends Record<string, any>>(rows: T[]): string {
  if (rows.length === 0) return "";
  const headers = Array.from(
    rows.reduce<Set<string>>((s, r) => {
      Object.keys(r).forEach((k) => s.add(k));
      return s;
    }, new Set())
  );
  const escape = (v: any) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
  ].join("\n");
}

export function downloadCsv(filename: string, rows: Record<string, any>[]) {
  if (!isBrowser) return;
  const blob = new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
